import { gql, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ErrorMessage,
  Modal,
  ModalActionButtonProps,
  ModalOpenProcess,
  ModalOpenProcessRef,
  Select,
  useRunWithFlag,
  useScreenWidth,
} from "@msys/ui";
import { TouchApp as TouchAppIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isEqual } from "lodash-es";
import React from "react";
import { useEffectOnce, useLockBodyScroll, useUpdateEffect } from "react-use";
import { PimSearchProductsSorting } from "../../../../clients/graphqlTypes.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { LayoutWithLeftColumn } from "../../../commons/layout/LayoutWithLeftColumn.js";
import { track } from "../../../track.js";
import { SectionTab } from "../../doc-items/SearchProductsAndTemplates.js";
import { SupplierCatalogueValue } from "../../suppliers/SupplierCatalogueSelect.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { OrganisationSupplierProductFavouriteButton } from "../buttons/OrganisationSupplierProductFavouriteButton.js";
import { ProductPreferenceButton } from "../buttons/ProductPreferenceButton.js";
import { ProductsListView } from "../components/ProductsListView.js";
import {
  isAnyFilterSet,
  ProductFiltersFormValues,
} from "../filters/ProductFilterFields.js";
import { productFiltersDefaultValue } from "../filters/ProductFilters.js";
import { useProductSearchType } from "../filters/useProductSearchTypes.js";
import {
  DEFAULT_PRODUCT_SORTING,
  useCanCreateSupplierCatalogueProduct,
} from "../helper.js";
import { getPreferenceProductType } from "../productPreferences.js";
import { useProductPreferences } from "../useProductPreferences.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../Product.generated.js";
import { ProductCategoryTreeChip } from "../ProductCategoryTreeChip.js";
import {
  ProductCategoryTreeSelect,
  useProductCategoryTree,
} from "../ProductCategoryTreeSelect.js";
import { ProductSearchLandingPage } from "../ProductSearchLandingPage.js";
import {
  ChildrenFn,
  ProductSearchListHeader,
} from "../ProductSearchListHeader.js";
import {
  ProductAskTypeProcess,
  ProductAskTypeProcessRef,
} from "./ProductAskTypeModal.js";
import {
  ProductCreateModal,
  ProductCreateModalProps,
} from "./ProductCreateModal.js";
import { ProductOverviewModal } from "./ProductOverviewModal.js";
import { useProductSearchModalQuery } from "./ProductSearchModal.generated.js";
import {
  SupplierCatalogueProductCreateModal,
  SupplierCatalogueProductCreateModalProps,
} from "./SupplierCatalogueProductCreateModal.js";
import { ButtonCircleWrapper } from "../../../commons/button/ButtonCircleWrapper.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

interface Props {
  initialSearchValue?: string;
  initialFilters?: Partial<ProductFiltersFormValues>;
  /**
   * Omitted where the modal is opened to work on the catalogue rather than to pick from it:
   * without it no row offers a selection, because there is nothing for a selection to fill.
   */
  handleProductChoice?: (
    product: Pick<
      ProductSearchItem__ProductSearchResultFragment,
      "articleNumber" | "supplierId"
    >,
    filters: ProductFiltersFormValues
  ) => Promise<void> | void;
  handleProductCreate?: ProductCreateModalProps["handleSubmit"];
  handleFiltersSave?: (values: ProductFiltersFormValues) => Promise<unknown>;
  handleClose: () => void;
  handleCancel?: () => void;
  cancelTitle?: string;
  canChangeProductType?: boolean;
  title?: string;
  /**
   * Keys the preference control to this product type instead of to the filtered one, so the
   * filter stays free to be widened or dropped while the preference target does not move.
   */
  pinnedPreferenceProductTypeId?: string | null;
  /**
   * Set where the search is driven by a product placeholder: preferred articles stay
   * reachable under the placeholder's product type even when classified otherwise.
   */
  includePreferences?: boolean;
}

export const ProductSearchModal = ({
  initialSearchValue = "",
  initialFilters,
  handleProductChoice,
  handleProductCreate,
  handleFiltersSave,
  handleClose,
  handleCancel,
  cancelTitle,
  canChangeProductType = true,
  title,
  pinnedPreferenceProductTypeId = null,
  includePreferences = false,
}: Props) => {
  const { t } = useTranslate(["Product", "Global", "ProductSearch"]);
  const { isMinTablet, isMinLargeDesktop } = useScreenWidth();

  const [productOverviewModalProduct, setProductOverviewModalProduct] =
    React.useState<{ articleNumber: string; supplierId: string } | null>(null);

  const { productSearchTypeOptions, productSearchType, setProductSearchType } =
    useProductSearchType();

  const [paginationModel, setPaginationModel] = React.useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 25 });

  const [sortModel, setSortModel] = React.useState<PimSearchProductsSorting[]>(
    DEFAULT_PRODUCT_SORTING
  );

  const [searchTerm, setSearchTerm] =
    React.useState<string>(initialSearchValue);

  const initialFilterState = React.useMemo<ProductFiltersFormValues>(
    () => ({
      ...productFiltersDefaultValue,
      ...initialFilters,
    }),
    [initialFilters]
  );
  const [filters, setFilters] =
    React.useState<ProductFiltersFormValues>(initialFilterState);

  const productSearchVariables = React.useMemo(
    () => ({
      searchTerm:
        productSearchType === "fulltextSearch" ? searchTerm : undefined,
      specificFieldQueryFields:
        productSearchType !== "fulltextSearch"
          ? [{ field: productSearchType, value: searchTerm }]
          : undefined,
      filters,
    }),
    [filters, productSearchType, searchTerm]
  );

  const [section, setSection] = React.useState<SectionTab>(
    Boolean(searchTerm) || isAnyFilterSet(filters) ? "results" : "landing"
  );

  // automatically switching to results when needed
  useEffectOnce(() => {
    if (Boolean(searchTerm) || isAnyFilterSet(filters)) {
      setSection("results");
    }
  });
  useUpdateEffect(() => {
    if (
      (Boolean(searchTerm) || isAnyFilterSet(filters)) &&
      section === "landing"
    )
      setSection("results");
  }, [searchTerm, filters]);

  const client = useApolloClient();
  const query = useProductSearchModalQuery({
    client,
    variables: {
      ...productSearchVariables,
      limit: paginationModel.pageSize,
      offset: paginationModel.pageSize * paginationModel.page,
      sorting: sortModel,
      includePreferences,
    },
    skip: section !== "results",
  });

  const resetFilters = () => {
    setFilters(initialFilterState);
  };

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "products-list-modal",
      "list",
      "list"
    );

  const products =
    getDataOrNull(
      (query.data ?? query.previousData)?.pimSearchProducts
    )?.edges.map(e => e.node) ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchProducts)
      ?.totalCount ?? 0;

  const onProductSelect = handleProductChoice
    ? async (product: ProductSearchItem__ProductSearchResultFragment) => {
        track({
          eventName: "AddProduct",
          data: { product, variables: productSearchVariables },
        });
        await handleProductChoice(product, filters);
        handleClose();
      }
    : undefined;

  React.useEffect(() => {
    const totalCount = getDataOrNull(query.data?.pimSearchProducts)?.totalCount;
    if (
      totalCount &&
      totalCount < paginationModel.pageSize * paginationModel.page &&
      paginationModel.page > 0
    ) {
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    }
  }, [query.data, paginationModel, setPaginationModel]);

  const actionButtons: ModalActionButtonProps[] = [
    {
      label:
        cancelTitle ??
        t("Cancel", {
          ns: "Global",
        }),
      handleClick: () => {
        handleCancel?.();
        handleClose();
      },
      buttonProps: { variant: "text" },
    },
  ];
  if (handleFiltersSave) {
    actionButtons.push({
      label: t("Save filters", { ns: "ProductSearch" }),
      handleClick: () => {
        handleFiltersSave(filters);
        handleClose();
      },
      buttonProps: {
        variant: "contained",
        disabled:
          !filters.productTypeIds ||
          filters.productTypeIds.length === 0 ||
          isEqual(filters, initialFilters),
      },
    });
  }

  const [searchEnabled, setSearchEnabled] = React.useState<boolean>(false);
  useLockBodyScroll(searchEnabled);

  const theme = useTheme();

  const handleBackClick =
    section === "results"
      ? () => {
          resetFilters();
          setSection("landing");
        }
      : undefined;

  const handleSearchSubmit = () => {
    if (section === "landing") setSection("results");
  };

  const productCategoryProps = useProductCategoryTree(
    filters.supplierIds?.[0] ?? null,
    filters.supplierProductCategoryKey ?? null
  );

  const showCategoriesSelect = Boolean(
    productCategoryProps.supplier &&
    (productCategoryProps.childCategories.length ||
      productCategoryProps.productCategory)
  );

  const handleCategoryTreeSelect = ({
    supplierId,
    supplierProductCategoryKey,
  }: {
    supplierProductCategoryKey?: string | null;
    supplierId?: string | null;
  }) => {
    const newFilters = { ...filters };
    if (supplierId === null) {
      newFilters.supplierIds = null;
    }
    if (supplierProductCategoryKey !== undefined) {
      newFilters.supplierProductCategoryKey = supplierProductCategoryKey;
    }
    setFilters(newFilters);
  };

  return (
    <Modal
      maxWidth="xl"
      dialogProps={{
        slotProps: { paper: { style: { height: "100%" } } },
      }}
      handleClose={handleClose}
      title={
        title ??
        t("Search Products", {
          ns: "ProductSearch",
        })
      }
      actionButtons={actionButtons}
      alwaysVisible
      titleProps={{
        sx: {
          position: "relative",
          zIndex: 2,
          backgroundColor: theme.palette.common.white,
        },
      }}
    >
      <ProductSearchModalListHeader
        initialFilterState={initialFilterState}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        hideFilters={section !== "results"}
        productSearchTypeOptions={productSearchTypeOptions}
        productSearchType={productSearchType}
        setProductSearchType={setProductSearchType}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        activeView={activeView}
        setActiveView={setActiveView}
        productSearchVariables={productSearchVariables}
        handleClose={handleClose}
        handleProductCreate={handleProductCreate}
        handleSupplierCatalogueProductCreate={
          handleProductChoice
            ? async (product, handleClose) => {
                await handleProductChoice(product, filters);
                handleClose();
              }
            : undefined
        }
        canChangeProductType={canChangeProductType}
        includePreferences={includePreferences}
        searchEnabled={searchEnabled}
        setSearchEnabled={setSearchEnabled}
        handleBackClick={handleBackClick}
        handleSearchSubmit={handleSearchSubmit}
        onProductSelect={product =>
          setProductOverviewModalProduct({
            articleNumber: product.articleNumber,
            supplierId: product.supplierId,
          })
        }
        AdditionalChips={
          !isMinTablet &&
          showCategoriesSelect &&
          productCategoryProps.supplier ? (
            <ProductCategoryTreeChip
              supplier={productCategoryProps.supplier}
              productCategory={productCategoryProps.productCategory}
              handleSelect={handleCategoryTreeSelect}
            />
          ) : undefined
        }
      >
        {({ SearchLine, FiltersLine, ChipsLine, SearchBackdrop }) => (
          <Stack
            direction={"column"}
            spacing={1}
            sx={{
              height: "100%",
            }}
          >
            <Box>
              <Box
                sx={{
                  overflow: "hidden",
                  px: { xs: 1, md: 1.5 },
                  mx: { xs: -1, md: -1.5 },
                  pb: 1,
                  mb: -1,
                  width: "100%",
                  boxSizing: "content-box",
                }}
              >
                {SearchLine}
              </Box>
            </Box>
            {SearchBackdrop}
            {query.error && <ErrorMessage message={query.error.message} />}
            {section === "results" ? (
              <LayoutWithLeftColumn
                name="ProductSearchModal"
                leftColumn={
                  showCategoriesSelect ? (
                    <ProductCategoryTreeSelect
                      {...productCategoryProps}
                      categoryKey={filters.supplierProductCategoryKey ?? null}
                      onSelect={handleCategoryTreeSelect}
                    />
                  ) : undefined
                }
                spacing={0}
                leftColumnWidth={isMinLargeDesktop ? "xs" : "xxs"}
              >
                <Stack
                  direction={"column"}
                  spacing={1}
                  sx={{
                    height: "100%",
                    width: "100%",
                    minHeight: 0,
                    minWidth: 0,
                  }}
                >
                  {FiltersLine}
                  {ChipsLine}
                  <ProductsList
                    items={products}
                    totalCount={totalCount}
                    activeView={activeView}
                    loading={query.loading}
                    onSelectItem={setProductOverviewModalProduct}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    sortModel={sortModel}
                    setSortModel={setSortModel}
                    onProductSelect={onProductSelect}
                    productTypeInContext={getPreferenceProductType(
                      pinnedPreferenceProductTypeId,
                      filters
                    )}
                  />
                </Stack>
              </LayoutWithLeftColumn>
            ) : section === "landing" ? (
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                }}
              >
                <ProductSearchLandingPage
                  onProductClick={product =>
                    setProductOverviewModalProduct(product)
                  }
                  renderProductSelectButton={
                    onProductSelect
                      ? product => (
                          <ButtonCircleWrapper>
                            <ProductSelectButton
                              product={product}
                              onProductSelect={onProductSelect}
                            />
                          </ButtonCircleWrapper>
                        )
                      : undefined
                  }
                  setProductSearchFilters={setFilters}
                />
              </Box>
            ) : null}
            {productOverviewModalProduct && (
              <ProductOverviewModal
                productArticleNumber={productOverviewModalProduct.articleNumber}
                productSupplierId={productOverviewModalProduct.supplierId}
                handleSubmit={
                  handleProductChoice
                    ? async product => {
                        track({
                          eventName: "AddProduct",
                          data: { product, variables: productSearchVariables },
                        });
                        await handleProductChoice(product, filters);
                        handleClose();
                      }
                    : undefined
                }
                handleClose={() => setProductOverviewModalProduct(null)}
              />
            )}
          </Stack>
        )}
      </ProductSearchModalListHeader>
    </Modal>
  );
};

function ProductSearchModalListHeader({
  handleProductCreate,
  handleSupplierCatalogueProductCreate,
  handleClose,
  children,
  ...props
}: Omit<
  React.ComponentProps<typeof ProductSearchListHeader>,
  "ActionButtons" | "allowedViews" | "children"
> & {
  handleProductCreate?: ProductCreateModalProps["handleSubmit"];
  handleSupplierCatalogueProductCreate?: SupplierCatalogueProductCreateModalProps["handleComplete"];
  handleClose: () => void;
  children: ChildrenFn;
}) {
  const { t } = useTranslate(["Global", "ProductSearch"]);

  const [initialSupplierCatalogue, setInitialSupplierCatalogue] =
    React.useState<SupplierCatalogueValue | null>(null);

  const { canCreateSupplierCatalogueProduct } =
    useCanCreateSupplierCatalogueProduct();

  const productCreateRef = React.useRef<ModalOpenProcessRef>();
  const supplierCatalogueProductCreateRef = React.useRef<ModalOpenProcessRef>();
  const productAskTypeProcessRef =
    React.useRef<ProductAskTypeProcessRef | null>(null);

  const handleCreateNewProduct = async () => {
    if (
      handleProductCreate &&
      handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct
    ) {
      // both possible - need to ask at first which product to create
      const values = await productAskTypeProcessRef.current?.open();
      if (values) {
        if (values.productType === "one-time") {
          productCreateRef.current?.open();
        } else if (values.productType === "catalogue") {
          setInitialSupplierCatalogue(values.supplierCatalogue || null);
          setTimeout(() => {
            supplierCatalogueProductCreateRef.current?.open();
          });
        }
      }
    } else if (handleProductCreate) {
      // one-time used product
      productCreateRef.current?.open();
    } else if (
      handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct
    ) {
      // supplier catalogue product
      setInitialSupplierCatalogue(null);
      setTimeout(() => {
        supplierCatalogueProductCreateRef.current?.open();
      });
    }
  };

  const ActionButtons =
    handleProductCreate ||
    (handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct) ? (
      <ButtonCreate
        title={t("New product", { ns: "ProductSearch" })}
        onClick={handleCreateNewProduct}
      />
    ) : undefined;

  return (
    <>
      <ProductSearchListHeader
        {...props}
        children={children}
        allowedViews={ALLOWED_VIEWS}
        ActionButtons={ActionButtons}
        px={{ xs: 1, md: 1.5 }}
        mx={{ xs: -1, md: -1.5 }}
        pt={1}
        mt={-1}
        onProductSelect={props.onProductSelect}
      />
      {handleProductCreate && (
        <ModalOpenProcess
          ref={productCreateRef}
          Modal={ProductCreateModal}
          modalProps={{
            handleSubmit: async values => {
              await handleProductCreate(values);
              handleClose();
            },
          }}
        />
      )}
      {handleSupplierCatalogueProductCreate &&
        canCreateSupplierCatalogueProduct && (
          <ModalOpenProcess
            ref={supplierCatalogueProductCreateRef}
            Modal={SupplierCatalogueProductCreateModal}
            modalProps={{
              initialSupplierCatalogue,
              handleComplete: handleSupplierCatalogueProductCreate,
            }}
          />
        )}
      <ProductAskTypeProcess ref={productAskTypeProcessRef} />
    </>
  );
}

function ProductSelectButton({
  product,
  onProductSelect,
}: {
  product: ProductSearchItem__ProductSearchResultFragment;
  onProductSelect(
    product: ProductSearchItem__ProductSearchResultFragment
  ): void | Promise<void>;
}) {
  const { t } = useTranslate(["Global", "ProductSearch"]);
  const [processing, run] = useRunWithFlag();
  return (
    <IconButton
      color={"primary"}
      size={"small"}
      disabled={processing}
      onClick={async () => {
        await run(async () => {
          await onProductSelect(product);
        });
      }}
      title={t("Select", { ns: "ProductSearch" })}
      aria-label={t("Select", { ns: "ProductSearch" })}
    >
      <TouchAppIcon fontSize="small" />
    </IconButton>
  );
}

function ProductsList({
  items,
  totalCount,
  activeView,
  loading,
  onSelectItem,
  paginationModel,
  setPaginationModel,
  sortModel,
  setSortModel,
  onProductSelect,
  productTypeInContext,
}: {
  items: ProductSearchItem__ProductSearchResultFragment[];
  totalCount: number;
  activeView: CollectionView;
  loading: boolean;
  onSelectItem(product: ProductSearchItem__ProductSearchResultFragment): void;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (newPaginationModel: {
    page: number;
    pageSize: number;
  }) => void;
  sortModel: PimSearchProductsSorting[];
  setSortModel: (model: PimSearchProductsSorting[]) => void;
  onProductSelect?: (
    product: ProductSearchItem__ProductSearchResultFragment
  ) => void | Promise<void>;
  productTypeInContext: string | null;
}) {
  const stateStore = useDataGridStateStore("ProductSearchModal");
  const preferences = useProductPreferences(
    productTypeInContext === null
      ? null
      : { productTypeId: productTypeInContext }
  );
  const ActionButton = React.useCallback(
    ({
      product,
    }: {
      product: ProductSearchItem__ProductSearchResultFragment;
    }) => (
      <>
        <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
          <ButtonCircleWrapper>
            <OrganisationSupplierProductFavouriteButton
              product={product}
              canEdit={true}
            />
          </ButtonCircleWrapper>
        </RestrictedByOrganisationPermissionWithDebug>
        {productTypeInContext ? (
          <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
            <ButtonCircleWrapper>
              <ProductPreferenceButton
                productTypeId={productTypeInContext}
                product={product}
                preferences={preferences}
              />
            </ButtonCircleWrapper>
          </RestrictedByOrganisationPermissionWithDebug>
        ) : null}
        {onProductSelect ? (
          <ButtonCircleWrapper>
            <ProductSelectButton
              product={product}
              onProductSelect={onProductSelect}
            />
          </ButtonCircleWrapper>
        ) : null}
      </>
    ),
    [onProductSelect, productTypeInContext, preferences]
  );
  return (
    <ProductsListView
      items={items}
      totalCount={totalCount}
      activeView={activeView}
      loading={loading}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      sortModel={sortModel}
      setSortModel={setSortModel}
      CardActionButton={ActionButton}
      TableActions={ActionButton}
      stateStore={stateStore}
      tableActionsWidth={
        70 + (productTypeInContext ? 40 : 0) + (onProductSelect ? 40 : 0)
      }
      onClick={onSelectItem}
    />
  );
}
